import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, Check, Flag, Pencil } from "lucide-react";
import { api, formatApiError, absoluteAssetUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { displayCurrency } from "../utils/pricing";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { useAuth } from "../lib/auth-context";

const SECTION_LABELS = {
    metadata: "Metadata",
    purpose_scope: "Purpose & scope",
    responsibilities: "Responsibilities",
    references: "References",
    apparatus_materials: "Apparatus & materials",
    safety_precautions: "Safety precautions",
    procedure: "Procedure",
    calculations: "Calculations",
    result_interpretation: "Result interpretation",
    shutdown_procedure: "Shutdown",
    test_precautions: "Test precautions",
    notes: "Notes",
    uncertainties: "Uncertainties",
    unknown: "Other",
};

const CLASS_STYLES = {
    TECHNICAL_LOCKED: "bg-indigo-50 text-indigo-700",
    RUNWAY_SAFE: "bg-emerald-50 text-emerald-700",
    MANUAL_REVIEW: "bg-amber-50 text-amber-700",
    ILLUSTRATIVE: "bg-sky-50 text-sky-700",
    UNKNOWN: "bg-neutral-100 text-neutral-600",
};

export default function ReviewWorkspace() {
    const { id } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const [doc, setDoc] = useState(null);
    const [review, setReview] = useState(null);
    const [sections, setSections] = useState([]);
    const [steps, setSteps] = useState([]);
    const [media, setMedia] = useState([]);
    const [blockers, setBlockers] = useState({ can_approve: false, blockers: [], unreviewed_count: 0 });
    const [loading, setLoading] = useState(true);
    const [overrideReason, setOverrideReason] = useState("");
    const [showOverride, setShowOverride] = useState(false);

    // UX helper — a review PATCH can 409 with "No review in progress"
    // when the document has already been approved.  Instead of
    // surfacing an error toast, silently POST /reviews to reopen it
    // and retry the original call once.  All review-state actions
    // (Accept / Edited / Flag / Replace / step + section edits) go
    // through this wrapper so admins never see a dead button on a
    // closed document.
    const withAutoReopen = useCallback(async (fn) => {
        try {
            return await fn();
        } catch (e) {
            const status = e?.response?.status;
            const detail = String(e?.response?.data?.detail || "");
            if (status === 409 && /no review in progress/i.test(detail)) {
                await api.post(`/documents/${id}/reviews`);
                return await fn();
            }
            throw e;
        }
    }, [id]);

    const load = useCallback(async () => {
        try {
            const d = await api.get(`/documents/${id}`);
            setDoc(d.data);
            const vid = d.data.current_version?.id;
            if (!vid) {
                setLoading(false);
                return;
            }
            let r = null;
            try {
                r = (await api.get(`/documents/${id}/reviews/current`)).data;
            } catch {
                r = (await api.post(`/documents/${id}/reviews`)).data;
            }
            setReview(r);
            const [s, st, m, b] = await Promise.all([
                api.get(`/documents/${id}/versions/${vid}/sections`),
                api.get(`/documents/${id}/versions/${vid}/steps`),
                api.get(`/documents/${id}/versions/${vid}/media`),
                api.get(`/documents/${id}/reviews/current/blockers`),
            ]);
            setSections(s.data);
            setSteps(st.data);
            setMedia(m.data);
            setBlockers(b.data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        load();
    }, [load]);

    const patchStep = async (stepId, body) => {
        try {
            const { data } = await withAutoReopen(
                () => api.patch(`/procedure-steps/${stepId}`, body),
            );
            setSteps((prev) => prev.map((s) => (s.id === stepId ? { ...s, ...data } : s)));
            const b = await api.get(`/documents/${id}/reviews/current/blockers`);
            setBlockers(b.data);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const patchSection = async (sectionId, body) => {
        try {
            const { data } = await withAutoReopen(
                () => api.patch(`/document-sections/${sectionId}`, body),
            );
            setSections((prev) => prev.map((s) => (s.id === sectionId ? { ...s, ...data } : s)));
            const b = await api.get(`/documents/${id}/reviews/current/blockers`);
            setBlockers(b.data);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const patchMedia = async (mediaId, body) => {
        try {
            const { data } = await withAutoReopen(
                () => api.patch(`/media-assets/${mediaId}`, body),
            );
            setMedia((prev) => prev.map((m) => (m.id === mediaId ? { ...m, ...data, signed_url: m.signed_url } : m)));
            const b = await api.get(`/documents/${id}/reviews/current/blockers`);
            setBlockers(b.data);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const replaceMedia = async (mediaId, file) => {
        try {
            const fd = new FormData();
            fd.append("file", file);
            await withAutoReopen(
                () => api.post(`/media-assets/${mediaId}/replace`, fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                }),
            );
            // Remove old, prepend new (or refetch)
            const vid = doc.current_version?.id;
            const [m, b] = await Promise.all([
                api.get(`/documents/${id}/versions/${vid}/media`),
                api.get(`/documents/${id}/reviews/current/blockers`),
            ]);
            setMedia(m.data);
            setBlockers(b.data);
            toast.success("Image replaced — awaiting manual review");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const approve = async (withOverride = false) => {
        try {
            const url = `/documents/${id}/reviews/current/approve${withOverride ? "?override=true" : ""}`;
            const payload = withOverride ? { reason: overrideReason } : {};
            await api.post(url, payload);
            toast.success("Approved");
            nav("/knowledge/reviews");
        } catch (e) {
            if (e?.response?.status === 409 && e.response.data?.detail?.blockers) {
                toast.error("Blockers remain — resolve or override.");
            } else {
                toast.error(formatApiError(e));
            }
        }
    };

    const nonMetaSections = useMemo(() => sections.filter((s) => s.kind !== "metadata"), [sections]);
    const stepsById = useMemo(() => Object.fromEntries(steps.map((s) => [s.id, s])), [steps]);

    if (loading || !doc) {
        return <div data-testid="review-loading" className="py-24 text-center text-neutral-500 text-sm">Loading…</div>;
    }

    return (
        <div data-testid="review-workspace" className="space-y-6 -mx-2">
            <div className="flex items-center justify-between">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => nav("/knowledge/reviews")}
                    data-testid="back-to-reviews"
                    className="text-neutral-600 hover:text-neutral-900"
                >
                    <ArrowLeft size={14} className="mr-1" />
                    Reviews
                </Button>
                <div className="text-right">
                    <div className="text-[11px] uppercase tracking-widest text-neutral-500">
                        {doc.document.type} · {doc.document.parsed_doc_number || "—"} · {doc.document.parsed_revision || "—"}
                    </div>
                    <div className="text-lg font-medium text-neutral-900 leading-tight">
                        {doc.document.parsed_title || doc.document.original_filename}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-[calc(100vh-260px)]">
                {/* Source pane */}
                <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
                    <div className="border-b border-neutral-200 px-5 py-2.5 flex items-center justify-between">
                        <span className="text-[11px] font-medium uppercase tracking-widest text-neutral-500">Source</span>
                        <span className="mono text-[10px] text-neutral-400">read-only</span>
                    </div>
                    <div data-testid="source-pane" className="p-5 overflow-y-auto h-full space-y-5">
                        {nonMetaSections.map((s) => (
                            <div key={s.id}>
                                <div className="text-sm font-semibold text-neutral-900">
                                    {SECTION_LABELS[s.kind] || s.kind}
                                </div>
                                {s.source_heading_path?.length > 0 && (
                                    <div className="mt-0.5 text-[11px] text-neutral-500">
                                        {s.source_heading_path.join(" › ")}
                                    </div>
                                )}
                                <pre className="mt-2 whitespace-pre-wrap font-sans text-sm text-neutral-700 leading-relaxed">
                                    {s.raw_text || <span className="text-neutral-400">— empty —</span>}
                                </pre>
                                {s.kind === "procedure" && (
                                    <div className="mt-3 space-y-2 border-l-2 border-neutral-100 pl-3">
                                        {steps.map((st) => (
                                            <div key={st.id} className="text-sm text-neutral-700">
                                                <span className="mono font-medium">{st.step_number}</span>{" "}
                                                {st.original_instruction}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Structured pane */}
                <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden flex flex-col">
                    <Tabs defaultValue="procedure" className="flex-1 flex flex-col overflow-hidden">
                        <TabsList data-testid="workspace-tabs" className="border-b border-neutral-200 rounded-none justify-start bg-transparent p-0 h-auto shrink-0">
                            {["metadata", "sections", "procedure", "images"].map((t) => (
                                <TabsTrigger
                                    key={t}
                                    value={t}
                                    data-testid={`ws-tab-${t}`}
                                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-neutral-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 text-sm capitalize"
                                >
                                    {t}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                        <TabsContent value="metadata" className="p-5 overflow-y-auto">
                            <MetadataView
                                section={sections.find((s) => s.kind === "metadata")}
                                onPatch={patchSection}
                            />
                        </TabsContent>
                        <TabsContent value="sections" className="p-5 overflow-y-auto space-y-3">
                            {nonMetaSections.map((s) => (
                                <SectionCard key={s.id} section={s} onPatch={patchSection} />
                            ))}
                        </TabsContent>
                        <TabsContent value="procedure" className="p-5 overflow-y-auto space-y-4">
                            {steps.map((s) => (
                                <StepCard
                                    key={s.id}
                                    step={s}
                                    media={media.filter((m) => m.associated_step_id === s.id)}
                                    onPatch={patchStep}
                                />
                            ))}
                        </TabsContent>
                        <TabsContent value="images" className="p-5 overflow-y-auto">
                            <div className="grid grid-cols-2 gap-4">
                                {media.map((m) => (
                                    <MediaCard
                                        key={m.id}
                                        media={m}
                                        stepOptions={steps}
                                        onPatch={patchMedia}
                                        onReplace={replaceMedia}
                                    />
                                ))}
                            </div>
                        </TabsContent>
                    </Tabs>
                </div>
            </div>

            {/* Sticky footer */}
            <div data-testid="review-footer" className="sticky bottom-0 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-neutral-200 bg-white px-5 py-3 shadow-sm">
                <div className="flex items-center gap-4 text-sm text-neutral-700">
                    <div>
                        <span className="mono font-medium">{blockers.unreviewed_count}</span> unreviewed
                    </div>
                    <div>
                        <span className="mono font-medium">{blockers.blockers.length}</span> blockers
                    </div>
                    {doc.document.extraction_cost_estimate ? (
                        <div className="mono text-[11px] text-neutral-500">
                                            AI cost est. ${displayCurrency(doc.document.extraction_cost_estimate, 4)}
                        </div>
                    ) : null}
                </div>
                <div className="flex items-center gap-2">
                    {blockers.blockers.length > 0 && (
                        <details className="relative">
                            <summary className="cursor-pointer rounded-md border border-neutral-200 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-50" data-testid="blockers-toggle">
                                Preview blockers
                            </summary>
                            <div className="absolute bottom-full right-0 mb-2 w-96 rounded-lg border border-neutral-200 bg-white p-3 shadow-lg z-10">
                                <div className="max-h-64 overflow-y-auto text-sm space-y-1.5">
                                    {blockers.blockers.map((b, i) => (
                                        <div key={i} className="flex items-start gap-2">
                                            <AlertTriangle size={14} className="mt-0.5 text-amber-600 shrink-0" />
                                            <div>
                                                <div className="font-medium text-neutral-900">{b.label}</div>
                                                <div className="text-[11px] text-neutral-500">{b.reason}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </details>
                    )}
                    {!blockers.can_approve && user?.role === "ORG_ADMIN" && (
                        <>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShowOverride((v) => !v)}
                                data-testid="override-toggle"
                                className="text-amber-700"
                            >
                                Override
                            </Button>
                        </>
                    )}
                    <Button
                        onClick={() => approve(false)}
                        data-testid="approve-btn"
                        disabled={!blockers.can_approve}
                        className="bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-40"
                    >
                        Approve extraction
                    </Button>
                </div>
            </div>
            {showOverride && (
                <div data-testid="override-panel" className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3">
                    <div className="text-sm font-medium text-amber-900">
                        Override remaining blockers as ORG_ADMIN
                    </div>
                    <div className="text-xs text-amber-800">
                        This will be recorded in the audit log. Provide a reason (min 8 characters).
                    </div>
                    <Textarea
                        data-testid="override-reason"
                        rows={3}
                        value={overrideReason}
                        onChange={(e) => setOverrideReason(e.target.value)}
                        placeholder="Why is this override justified?"
                    />
                    <Button
                        onClick={() => approve(true)}
                        disabled={overrideReason.trim().length < 8}
                        data-testid="approve-override-btn"
                        className="bg-amber-600 text-white hover:bg-amber-700"
                    >
                        Approve with override
                    </Button>
                </div>
            )}
        </div>
    );
}

function ReviewActions({ state, onAccept, onEdit, onFlag, testidBase }) {
    return (
        <div className="flex items-center gap-2">
            <Button
                size="sm"
                variant={state === "ACCEPTED" ? "default" : "outline"}
                onClick={onAccept}
                data-testid={`${testidBase}-accept`}
                className={state === "ACCEPTED" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}
            >
                <Check size={13} className="mr-1" /> Accept
            </Button>
            <Button
                size="sm"
                variant={state === "EDITED" ? "default" : "outline"}
                onClick={onEdit}
                data-testid={`${testidBase}-edit`}
            >
                <Pencil size={13} className="mr-1" /> Edited
            </Button>
            <Button
                size="sm"
                variant={state === "FLAGGED" ? "default" : "outline"}
                onClick={onFlag}
                data-testid={`${testidBase}-flag`}
                className={state === "FLAGGED" ? "bg-red-600 hover:bg-red-700 text-white" : "text-red-600"}
            >
                <Flag size={13} className="mr-1" /> Flag
            </Button>
        </div>
    );
}

function ConfidenceChip({ value }) {
    const pct = value == null ? null : Math.round(value * 100);
    const cls =
        value >= 0.85
            ? "bg-emerald-50 text-emerald-700"
            : value >= 0.7
              ? "bg-amber-50 text-amber-700"
              : "bg-red-50 text-red-700";
    if (pct == null) return null;
    return (
        <span className={`mono inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-medium ${cls}`}>
            {pct}% conf.
        </span>
    );
}

function StepCard({ step, media, onPatch }) {
    const [validated, setValidated] = useState(step.validated_instruction || "");
    const [learner, setLearner] = useState(step.learner_friendly_instruction || "");
    useEffect(() => {
        setValidated(step.validated_instruction || "");
        setLearner(step.learner_friendly_instruction || "");
    }, [step.validated_instruction, step.learner_friendly_instruction]);

    return (
        <div data-testid={`step-card-${step.step_number}`} className="rounded-lg border border-neutral-200 bg-white p-4 space-y-3">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className="mono font-semibold text-neutral-900">Step {step.step_number}</span>
                    <ConfidenceChip value={step.confidence} />
                    <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-medium ${
                        step.review_state === "ACCEPTED" ? "bg-emerald-50 text-emerald-700" :
                        step.review_state === "EDITED" ? "bg-blue-50 text-blue-700" :
                        step.review_state === "FLAGGED" ? "bg-red-50 text-red-700" :
                        "bg-neutral-100 text-neutral-500"}`}>
                        {step.review_state}
                    </span>
                </div>
                <div className="flex items-center gap-3 text-xs">
                    <label className="flex items-center gap-1 cursor-pointer" data-testid={`step-safety-toggle-${step.step_number}`}>
                        <input
                            type="checkbox"
                            checked={!!step.safety_related}
                            onChange={(e) => onPatch(step.id, { safety_related: e.target.checked })}
                        /> Safety
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer" data-testid={`step-lock-toggle-${step.step_number}`}>
                        <input
                            type="checkbox"
                            checked={!!step.technical_lock_required}
                            onChange={(e) => onPatch(step.id, { technical_lock_required: e.target.checked })}
                        /> Tech-locked
                    </label>
                </div>
            </div>

            {media.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {media.map((m) => (
                        <img
                            key={m.id}
                            src={absoluteAssetUrl(m.signed_url)}
                            alt=""
                            className="h-14 w-20 rounded border border-neutral-200 object-cover"
                        />
                    ))}
                </div>
            )}

            <div>
                <div className="text-[10px] font-medium uppercase tracking-widest text-neutral-500 mb-1">Source instruction</div>
                <div className="rounded-md bg-neutral-50 border border-neutral-200 p-2.5 mono text-sm text-neutral-800 leading-relaxed">
                    {step.original_instruction}
                </div>
            </div>

            <div>
                <div className="text-[10px] font-medium uppercase tracking-widest text-neutral-500 mb-1">AI learning interpretation</div>
                <Textarea
                    rows={2}
                    value={validated}
                    onChange={(e) => setValidated(e.target.value)}
                    onBlur={() => {
                        if (validated !== (step.validated_instruction || "")) {
                            onPatch(step.id, { validated_instruction: validated, review_state: "EDITED" });
                        }
                    }}
                    data-testid={`step-validated-${step.step_number}`}
                />
                <Textarea
                    rows={2}
                    value={learner}
                    onChange={(e) => setLearner(e.target.value)}
                    onBlur={() => {
                        if (learner !== (step.learner_friendly_instruction || "")) {
                            onPatch(step.id, { learner_friendly_instruction: learner, review_state: "EDITED" });
                        }
                    }}
                    placeholder="Learner-friendly rewrite"
                    className="mt-2 text-neutral-600"
                    data-testid={`step-learner-${step.step_number}`}
                />
            </div>

            <div>
                <div className="text-[10px] font-medium uppercase tracking-widest text-neutral-500 mb-1">
                    Critical parameters
                </div>
                {(step.critical_parameters || []).length === 0 ? (
                    <div className="text-xs text-neutral-400">None extracted.</div>
                ) : (
                    <div className="rounded-md border border-neutral-200 overflow-hidden">
                        <table className="w-full text-xs">
                            <thead className="bg-neutral-50">
                                <tr>
                                    <th className="px-2 py-1 text-left font-medium text-neutral-500">Name</th>
                                    <th className="px-2 py-1 text-left font-medium text-neutral-500">Value</th>
                                    <th className="px-2 py-1 text-left font-medium text-neutral-500">Unit</th>
                                    <th className="px-2 py-1 text-left font-medium text-neutral-500">Source span</th>
                                    <th className="px-2 py-1 text-right font-medium text-neutral-500">Conf</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(step.critical_parameters || []).map((p, i) => (
                                    <tr key={i} className="border-t border-neutral-100">
                                        <td className="px-2 py-1 mono">{p.name}</td>
                                        <td className="px-2 py-1 mono">{p.value ?? "—"}</td>
                                        <td className="px-2 py-1 mono">{p.unit ?? "—"}</td>
                                        <td className="px-2 py-1 mono text-neutral-500">{p.source_span ?? "—"}</td>
                                        <td className="px-2 py-1 mono text-right">
                                            {p.confidence != null ? `${Math.round((p.confidence || 0) * 100)}%` : "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="flex items-center justify-between pt-1">
                <ReviewActions
                    state={step.review_state}
                    onAccept={() => onPatch(step.id, { review_state: "ACCEPTED" })}
                    onEdit={() => onPatch(step.id, { review_state: "EDITED" })}
                    onFlag={() => onPatch(step.id, { review_state: "FLAGGED" })}
                    testidBase={`step-${step.step_number}`}
                />
                {step.needs_review && step.review_state === "UNREVIEWED" && (
                    <span className="text-[11px] text-amber-700">Needs review</span>
                )}
            </div>
        </div>
    );
}

function SectionCard({ section, onPatch }) {
    const [validated, setValidated] = useState(section.validated_summary || "");
    const [learner, setLearner] = useState(section.learner_friendly_summary || "");
    useEffect(() => {
        setValidated(section.validated_summary || "");
        setLearner(section.learner_friendly_summary || "");
    }, [section.validated_summary, section.learner_friendly_summary]);
    return (
        <div data-testid={`section-card-${section.kind}`} className="rounded-lg border border-neutral-200 bg-white p-4 space-y-2">
            <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-neutral-900">{SECTION_LABELS[section.kind] || section.kind}</div>
                <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-medium ${
                    section.review_state === "ACCEPTED" ? "bg-emerald-50 text-emerald-700" :
                    section.review_state === "EDITED" ? "bg-blue-50 text-blue-700" :
                    section.review_state === "FLAGGED" ? "bg-red-50 text-red-700" :
                    "bg-neutral-100 text-neutral-500"}`}>
                    {section.review_state}
                </span>
            </div>
            <Textarea
                rows={2}
                value={validated}
                onChange={(e) => setValidated(e.target.value)}
                onBlur={() => {
                    if (validated !== (section.validated_summary || "")) {
                        onPatch(section.id, { validated_summary: validated, review_state: "EDITED" });
                    }
                }}
                placeholder="AI-validated summary"
                data-testid={`section-validated-${section.kind}`}
            />
            <Textarea
                rows={2}
                value={learner}
                onChange={(e) => setLearner(e.target.value)}
                onBlur={() => {
                    if (learner !== (section.learner_friendly_summary || "")) {
                        onPatch(section.id, { learner_friendly_summary: learner, review_state: "EDITED" });
                    }
                }}
                placeholder="Learner-friendly summary"
                className="text-neutral-600"
                data-testid={`section-learner-${section.kind}`}
            />
            <ReviewActions
                state={section.review_state}
                onAccept={() => onPatch(section.id, { review_state: "ACCEPTED" })}
                onEdit={() => onPatch(section.id, { review_state: "EDITED" })}
                onFlag={() => onPatch(section.id, { review_state: "FLAGGED" })}
                testidBase={`section-${section.kind}`}
            />
        </div>
    );
}

function MediaCard({ media, stepOptions, onPatch, onReplace }) {
    return (
        <div data-testid={`media-card-${media.id}`} className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
            <div className="aspect-video bg-neutral-50">
                {media.signed_url ? (
                    <img src={absoluteAssetUrl(media.signed_url)} alt="" className="h-full w-full object-cover" />
                ) : null}
            </div>
            <div className="p-3 space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-medium ${CLASS_STYLES[media.classification] || CLASS_STYLES.UNKNOWN}`}>
                        {media.classification || "UNKNOWN"}
                    </span>
                    <ConfidenceChip value={media.classification_confidence} />
                    <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-medium ${
                        media.review_state === "ACCEPTED" ? "bg-emerald-50 text-emerald-700" :
                        media.review_state === "EDITED" ? "bg-blue-50 text-blue-700" :
                        media.review_state === "FLAGGED" ? "bg-red-50 text-red-700" :
                        "bg-neutral-100 text-neutral-500"}`}>
                        {media.review_state}
                    </span>
                </div>
                {media.classification_reason && (
                    <div className="text-[11px] text-neutral-500">{media.classification_reason}</div>
                )}
                <div className="grid grid-cols-2 gap-2 text-xs">
                    <Select
                        value={media.classification || "UNKNOWN"}
                        onValueChange={(v) => onPatch(media.id, { classification: v, review_state: "EDITED" })}
                    >
                        <SelectTrigger data-testid={`media-class-${media.id}`}>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="TECHNICAL_LOCKED">Technical locked</SelectItem>
                            <SelectItem value="RUNWAY_SAFE">Cinematic clip safe</SelectItem>
                            <SelectItem value="MANUAL_REVIEW">Manual review</SelectItem>
                            <SelectItem value="UNKNOWN">Unknown</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select
                        value={media.associated_step_id || ""}
                        onValueChange={(v) => onPatch(media.id, { associated_step_id: v, review_state: "EDITED" })}
                    >
                        <SelectTrigger data-testid={`media-relink-${media.id}`}>
                            <SelectValue placeholder="Relink step" />
                        </SelectTrigger>
                        <SelectContent>
                            {stepOptions.map((s) => (
                                <SelectItem key={s.id} value={s.id}>Step {s.step_number}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <label className="flex items-center gap-2 text-xs text-neutral-700 pt-1">
                    <input
                        type="checkbox"
                        checked={!!media.allow_runway_motion}
                        onChange={(e) => onPatch(media.id, { allow_runway_motion: e.target.checked, review_state: "EDITED" })}
                        data-testid={`media-motion-${media.id}`}
                    />
                    Allow cinematic motion
                </label>
                <div className="flex items-center justify-between">
                    <ReviewActions
                        state={media.review_state}
                        onAccept={() => onPatch(media.id, { review_state: "ACCEPTED" })}
                        onEdit={() => onPatch(media.id, { review_state: "EDITED" })}
                        onFlag={() => onPatch(media.id, { review_state: "FLAGGED" })}
                        testidBase={`media-${media.id}`}
                    />
                    <label className="cursor-pointer text-xs text-neutral-600 hover:text-neutral-900" data-testid={`media-replace-label-${media.id}`}>
                        <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            className="hidden"
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) onReplace(media.id, f);
                                e.target.value = "";
                            }}
                            data-testid={`media-replace-input-${media.id}`}
                        />
                        Replace image
                    </label>
                </div>
            </div>
        </div>
    );
}

function MetadataView({ section, onPatch }) {
    const meta = section?.structured_json || {};
    return (
        <div className="space-y-3">
            <dl className="divide-y divide-neutral-100 rounded-lg border border-neutral-200 bg-white" data-testid="ws-metadata-list">
                {Object.entries(meta).map(([k, v]) => (
                    <div key={k} className="grid grid-cols-3 gap-4 px-4 py-2 items-center">
                        <dt className="text-[11px] font-medium uppercase tracking-widest text-neutral-500 col-span-1">{k.replaceAll("_", " ")}</dt>
                        <dd className="col-span-2 mono text-sm text-neutral-800">{String(v)}</dd>
                    </div>
                ))}
                {Object.keys(meta).length === 0 && (
                    <div className="p-6 text-sm text-neutral-500">No metadata extracted.</div>
                )}
            </dl>
            {section && (
                <ReviewActions
                    state={section.review_state}
                    onAccept={() => onPatch(section.id, { review_state: "ACCEPTED" })}
                    onEdit={() => onPatch(section.id, { review_state: "EDITED" })}
                    onFlag={() => onPatch(section.id, { review_state: "FLAGGED" })}
                    testidBase="metadata"
                />
            )}
        </div>
    );
}
